import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';

import FormStep from 'components/FormStep';
import FormParticipation from 'components/FormParticipation';
import FormQuestion from 'components/FormQuestion';
import Alert from 'components/Alert';
import Input from 'components/fields/Input';

const addAnswer = gql`
  mutation AddAnswer($participationId: ID!, $questionId: ID!, $answerId: ID = null, $file: String = null, $textAnswer: String = null) {
    createAnswer(
      participation_id: $participationId
      question_id: $questionId
      answer_id: $answerId
      file: $file
      text_answer: $textAnswer
    ) {
      next_question {
        id
      }
    }
  }
`;

const finishParticipation = gql`
  mutation finishParticipation(
    $participationId: ID!
    $contestId: ID!
    $name: String!
    $email: String!
    $phone: String!
    $cc: String = null
    $meoId: String = null
    $address: String = null
    $postal: String = null
    $local: String = null
    $hasExtraAnswers: Boolean!
    $extraAnswers: [ExtraAnswersInput]!
  ) {
    finishParticipation(
      participation_id: $participationId
      contest_id: $contestId
      name: $name
      email: $email
      phone: $phone
      cc: $cc
      meo_id: $meoId
      address: $address
      agreement: true
      postal: $postal
      local: $local
    ) {
      participation {
        id
      }
    }
    createExtraAnswers(participation_id: $participationId, extra_answers: $extraAnswers) @include(if: $hasExtraAnswers) {
      participation {
        id
        extra_answers {
          answer
        }
      }
    }
  }
`;

class Form extends Component {
  constructor(props) {
    super(props);

    this.formRef = React.createRef();
    this.state = {
      error: false,
      extraAnswers: [],
      formData: {},
      step: 1,
      submitted: false,
    };
  }

  onAnswerChange = (field, value, showErrors = false) => {
    let valueField;

    switch (field.type) {
      case 'file':
        valueField = 'file';
        break;
      case 'textarea':
        valueField = 'textAnswer';
        break;
      default:
        valueField = 'answerId';
    }

    let data = { questionId: field.name };
    data[valueField] = value;

    this.updateFormData(field, showErrors, data);
  };

  onExtraAnswerChange = (field, value, showErrors = false) => {
    let { extraAnswers } = this.state;
    const name = field.name;

    if (this.validate(field, showErrors)) {
      extraAnswers = extraAnswers.filter(item => item.extra_field_id !== name);
      extraAnswers.push({
        extra_field_id: name,
        answer: value,
      });

      this.setState({ extraAnswers });
    }
  };

  onParticipationChange = (field, value, showErrors = false) => {
    const { formData } = this.state;
    const name = field.name;

    formData[name] = value;

    this.updateFormData(field, showErrors, formData);
  };

  setError = (msg = 'Ocorreu um erro, por favor tente mais tarde.') => {
    this.setState({ loading: false, error: msg });
  };

  updateFormData = (field, showErrors, data) => {
    const valid = this.validate(field, showErrors);

    if (valid) {
      this.setState({ formData: data });
    }
  };

  validate = (field = this.formRef.current, updateErrors = true) => {
    const isValid = field.checkValidity();

    this.setState({ error: false });

    if (updateErrors && !isValid) {
      this.setError(field.validationMessage || 'Preencha os campos assinalados correctamente.');
    }

    return isValid;
  };

  render() {
    const { participationId, settings } = this.props;
    const { error, extraAnswers, formData, step, submitted } = this.state;
    const { id, questions, extra_fields } = settings;
    const stepsTotal = questions.length + 1;

    return (
      <>
        {submitted ? (
          <div className="form--success">A sua participação foi submetida com sucesso.</div>
        ) : (
          <form ref={this.formRef} onSubmit={this.submitForm} className={'form' + (error ? ' error' : '')} noValidate>
            <span className="form__label">
              <strong>{step}</strong> | {stepsTotal} <strong>Passo</strong>
            </span>
            {questions.map((question, i) => (
              <Mutation
                key={i}
                mutation={addAnswer}
                onCompleted={() => {
                  this.setState({
                    formData: {},
                    step: this.state.step + 1,
                  });
                }}
                onError={err => this.setState({ error: 'Ocorreu um erro, por favor tente mais tarde. - ' + err })}
              >
                {(addAnswer, { loading }) => (
                  <FormStep
                    active={step === i + 1}
                    loading={loading}
                    onSubmit={e => {
                      e.preventDefault();

                      if (this.validate()) {
                        addAnswer({ variables: { participationId, ...formData } });
                      }
                    }}
                  >
                    <FormQuestion {...question} onFieldChange={this.onAnswerChange} />
                  </FormStep>
                )}
              </Mutation>
            ))}
            <Mutation
              mutation={finishParticipation}
              onCompleted={() => {
                this.setState({
                  formData: {},
                  submitted: true,
                });
              }}
              onError={() => this.setState({ error: 'Ocorreu um erro, por favor tente mais tarde.' })}
            >
              {(finishParticipation, { loading }) => (
                <FormStep
                  active={step === stepsTotal}
                  islastStep
                  loading={loading}
                  onSubmit={e => {
                    e.preventDefault();

                    if (this.validate()) {
                      finishParticipation({
                        variables: {
                          participationId,
                          contestId: id,
                          ...formData,
                          hasExtraAnswers: extraAnswers.length > 0,
                          extraAnswers: extraAnswers,
                        },
                      });
                    }
                  }}
                >
                  <FormParticipation {...settings} onFieldChange={this.onParticipationChange} />
                  {extra_fields.length > 0 &&
                    extra_fields.map(field => (
                      <Input key={field.id} name={field.id} label={field.placeholder} onFieldChange={this.onExtraAnswerChange} />
                    ))}
                </FormStep>
              )}
            </Mutation>
          </form>
        )}
        {error && <Alert>{error}</Alert>}
      </>
    );
  }
}

Form.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default Form;
